import { Icon, useToast } from '@/src/components/ui';
import { useBoolean } from '@/src/hooks/use-boolean';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu';
import { useRouter } from 'next/navigation';
import React from 'react';
import { ESystem, IProjectFile } from 'types/src/types';

type Props = {
  projectFiles: IProjectFile[];
  children?: React.ReactNode;
  isAnonymous: boolean;
  isDownloaded?: ReturnType<typeof useBoolean>;
  isDownloading?: ReturnType<typeof useBoolean>;
  isSubmenu: boolean;
  submenuLabel?: React.ReactNode;
  isMyProject?: boolean;
};

export const DownloadsDropdown = ({
  projectFiles,
  children,
  isAnonymous,
  isDownloaded,
  isDownloading,
  isSubmenu,
  submenuLabel,
  isMyProject,
}: Props) => {
  const router = useRouter();
  const toast = useToast();

  const handleDownload = async (
    projectId: number,
    fileId: number,
    system: ESystem,
    fileUrl?: string
  ) => {
    if (isAnonymous) {
      router.push('/sign-in');
      return;
    }

    if (system === ESystem.IOS || system === ESystem.ANDROID) {
      if (fileUrl) window.open(fileUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      isDownloading?.onTrue();
      const res = await fetch(
        `/api/projects/${projectId}/download?fileId=${encodeURIComponent(fileId)}`,
        {
          method: 'GET',
          headers: { 'Cache-Control': 'no-store' },
        }
      );
      if (!res.ok) {
        toast.error('Download failed');
        const { error } = await res
          .json()
          .catch(() => ({ error: 'Download failed' }));
        throw new Error(error);
      }
      const { url, filename } = await res.json();
      const fileRes = await fetch(url, { credentials: 'omit' });
      if (!fileRes.ok) return;
      const blob = await fileRes.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'file.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      isDownloaded?.onTrue();
    } catch (error) {
      toast.error('Download failed');
    } finally {
      isDownloading?.onFalse();
    }
  };

  const items = projectFiles?.length ? (
    projectFiles.map(({ id, system, fileVersion, projectId, fileUrl }) => {
      if (
        !isMyProject &&
        (system === ESystem.IOS || system === ESystem.ANDROID)
      ) {
        return;
      }
      return (
        <DropdownMenuItem
          key={id ?? `${system}-${fileVersion ?? ''}`}
          onClick={() => handleDownload(projectId, id, system, fileUrl)}
          className="flex items-center justify-between gap-2 py-1 px-2 cursor-pointer text-sm"
        >
          <span>
            {system === ESystem.IOS || system === ESystem.ANDROID
              ? `Check ${system} link`
              : `Download for ${system}`}
          </span>
          <Icon name={system.toLowerCase()} className="size-4" />
        </DropdownMenuItem>
      );
    })
  ) : (
    <DropdownMenuItem disabled>No files available</DropdownMenuItem>
  );

  if (isSubmenu) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className="gap-2">
          {submenuLabel}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent alignOffset={-4}>
          {items}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="text-black/80 text-xs font-medium"
        align="center"
        side="bottom"
      >
        {items}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
